import { AppShell, Burger, Center, Group, Loader, NavLink, Text, Title } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { unwrap } from './api/client';
import { me } from './api/gen';
import { CreateOrganization } from './components/CreateOrganization';
import { OrgPicker } from './components/OrgPicker';
import { CALLBACK_PATH } from './lib/oidc';
import { useSession } from './lib/session';
import { AgentPage } from './pages/AgentPage';
import { AuthCallbackPage } from './pages/AuthCallbackPage';
import { AgentsPage } from './pages/AgentsPage';
import { EnvironmentsPage } from './pages/EnvironmentsPage';
import { KnowledgePage } from './pages/KnowledgePage';
import { ProjectsPage } from './pages/ProjectsPage';
import { SessionPage } from './pages/SessionPage';
import { SessionsPage } from './pages/SessionsPage';
import { SET_PASSWORD_PATH, SetPasswordPage } from './pages/SetPasswordPage';
import { SignInPage } from './pages/SignInPage';
import { VERIFY_PATH, VerifyEmailPage } from './pages/VerifyEmailPage';

const nav = [
  { to: '/projects', label: 'Projects' },
  { to: '/agents', label: 'Agents' },
  { to: '/sessions', label: 'Sessions' },
  { to: '/knowledge', label: 'Knowledge' },
  { to: '/environments', label: 'Environments' },
];

export function App() {
  const [opened, { toggle }] = useDisclosure();
  const { credential, ready, orgId } = useSession();
  const location = useLocation();
  // The same query OrgPicker holds; here it decides whether there is anything
  // to pick from, or an organisation to found first.
  const who = useQuery({
    queryKey: ['me'],
    queryFn: () => unwrap(me()),
    enabled: !!credential && ready,
  });

  // Zitadel sends the browser back here; the page finishes the sign-in.
  if (location.pathname === CALLBACK_PATH) return <AuthCallbackPage />;
  // The two links mindvm mails open these, signed in or not. The server puts
  // the same paths in its mail (internal/identity/mail.go).
  if (location.pathname === VERIFY_PATH) return <VerifyEmailPage />;
  if (location.pathname === SET_PASSWORD_PATH) return <SetPasswordPage />;
  if (!ready) {
    return (
      <Center h="100vh">
        <Loader />
      </Center>
    );
  }
  // Without a credential there is one page. Without an organisation there is
  // one choice: the picker, because every other route needs the header — or,
  // for somebody who belongs to none yet, founding one.
  if (!credential) return <SignInPage />;
  const noMemberships = who.isSuccess && who.data.memberships.length === 0;

  return (
    <AppShell
      header={{ height: 56 }}
      navbar={{ width: 220, breakpoint: 'sm', collapsed: { mobile: !opened } }}
      padding="md"
    >
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group>
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <Title order={4}>mindvm</Title>
          </Group>
          <OrgPicker />
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="xs">
        {nav.map((item) => (
          <NavLink
            key={item.to}
            component={Link}
            to={item.to}
            label={item.label}
            active={location.pathname.startsWith(item.to)}
          />
        ))}
      </AppShell.Navbar>

      <AppShell.Main>
        {orgId ? (
          <Routes>
            <Route path="/" element={<Navigate to="/projects" replace />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/agents" element={<AgentsPage />} />
            <Route path="/agents/:id" element={<AgentPage />} />
            <Route path="/sessions" element={<SessionsPage />} />
            <Route path="/sessions/:id" element={<SessionPage />} />
            <Route path="/knowledge" element={<KnowledgePage />} />
            <Route path="/environments" element={<EnvironmentsPage />} />
            <Route path="*" element={<Text>Nothing here.</Text>} />
          </Routes>
        ) : noMemberships ? (
          <CreateOrganization />
        ) : (
          <Text c="dimmed">Choose an organisation to begin.</Text>
        )}
      </AppShell.Main>
    </AppShell>
  );
}
